
import Vue from "vue";
import GoBackHeader from "@/components/shared/GoBackHeader.vue";
import AppOverlayLoader from "@/components/shared/AppOverlayLoader.vue";
import AiJobDescriptionModal from "@/components/recruiter/jobs/post_job/ai_generated/AiJobDescriptionModal.vue";
import { mapActions, mapGetters, mapMutations } from "vuex";
import {
  ACTIVATE_PUBLISHED_JOB,
  JOB_GENERATION_FROM_PROMPT,
  JOB_GENERATION_PROMPT_RESPONSE,
  RECOMMEND_JOB_PROFILES
} from "@/store/modules/recruiter/constants";
import { ROOT_ERROR, ROOT_NOTIFICATION } from "@/store/modules/root/constants";
import { VIEW_JOB_STATE } from "@/store/modules/common/constants";
import { ViewJobState } from "@/store/modules/common/interfaces";
import router from "@/router";

export default Vue.extend({
  name: "PostAiGenerativeJob",
  components: {
    GoBackHeader,
    AppOverlayLoader,
    AiJobDescriptionModal
  },
  data() {
    return {
      is_microphone_active: false,
      prompt: "",
      recognition: null as any,
      generation_loading: false,
      generation_loading_msg: "",
      generated_voices: new Set<string>(),
      screening_question: false,
      show_description_modal: false
    };
  },
  computed: {
    ...mapGetters("recruiter", {
      job_generation_prompt_resp: JOB_GENERATION_PROMPT_RESPONSE
    })
  },
  mounted() {
    this.init_speak_recognition();
  },
  beforeDestroy() {
    if (this.recognition) {
      if (this.is_microphone_active) this.speak_config();
      this.recognition.stop();
    }
  },
  methods: {
    ...mapMutations({
      root_error: ROOT_ERROR,
      root_notification: ROOT_NOTIFICATION
    }),
    ...mapMutations("recruiter", {
      set_ai_generated_prompt_resp: JOB_GENERATION_PROMPT_RESPONSE,
      set_recommend_job_profiles: RECOMMEND_JOB_PROFILES
    }),
    ...mapMutations("common", {
      set_view_job_state: VIEW_JOB_STATE
    }),
    ...mapActions("recruiter", {
      generate_job_from_prompt: JOB_GENERATION_FROM_PROMPT,
      activate_published_job: ACTIVATE_PUBLISHED_JOB
    }),
    init_speak_recognition() {
      // Prefer webkitSpeechRecognition (Chrome/Edge) like matching_algo_staging
      const SpeechRecognition =
        (window as any).webkitSpeechRecognition ||
        (window as any).SpeechRecognition;
      if (!SpeechRecognition) return;
      this.recognition = new SpeechRecognition();
      this.recognition.interimResults = true;
      this.recognition.continuous = true;
      this.recognition.lang = "en-US";
      this.recognition.onresult = (event: any) => {
        const results = Array.from(event.results) as { isFinal: boolean; 0?: { transcript: string } }[];
        let last_final_result: { 0?: { transcript: string } } | null = null;
        for (let i = results.length - 1; i >= 0; i--) {
          if (results[i].isFinal) {
            last_final_result = results[i];
            break;
          }
        }
        if (last_final_result && last_final_result[0]) {
          const transcript = last_final_result[0].transcript;
          if (transcript) {
            const formatted = transcript.trim().toLowerCase();
            if (!this.generated_voices.has(formatted)) {
              this.prompt = this.prompt ? this.prompt + " " + formatted : formatted;
              this.generated_voices.add(formatted);
            }
          }
        }
      };
      this.recognition.onerror = (event: any) => {
        this.is_microphone_active = false;
        const err = event?.error;
        if (err === "not-allowed") {
          this.root_error(this.$t("recruiter.post-job.ai-generated.voice-error-permission") || "Microphone access denied. Please allow microphone and try again.");
        } else if (err === "no-speech") {
          // User didn't speak; no need to show error
        } else if (err === "network") {
          this.root_error(this.$t("recruiter.post-job.ai-generated.voice-error-network") || "Voice input requires an internet connection.");
        } else if (err) {
          this.root_error(this.$t("recruiter.post-job.ai-generated.voice-error-generic") || "Voice input failed. Please try again or type your prompt.");
        }
      };
      this.recognition.onend = () => {
        if (this.is_microphone_active && this.recognition) {
          try {
            this.recognition.start();
          } catch (_) {
            this.is_microphone_active = false;
          }
        }
      };
    },
    speak_config() {
      if (!this.recognition) {
        this.root_error(this.$t("recruiter.post-job.ai-generated.voice-unsupported") || "Speech recognition is not supported. Use Chrome or Edge and ensure you're on HTTPS or localhost.");
        return;
      }
      this.is_microphone_active = !this.is_microphone_active;
      if (this.is_microphone_active) {
        try {
          this.recognition.start();
        } catch (e: any) {
          this.is_microphone_active = false;
          if (e?.message?.includes("already started")) return;
          this.root_error(this.$t("recruiter.post-job.ai-generated.voice-error-permission") || "Could not start microphone. Please allow microphone access and try again.");
        }
      } else {
        this.recognition.stop();
      }
    },
    repeat_prompt() {
      const videoElement = this.$refs.videoElement as HTMLVideoElement;
      if (videoElement) {
        videoElement.currentTime = 0;
        videoElement.play();
        videoElement.onended = () => {
          if (!this.is_microphone_active) this.speak_config();
        };
      }
    },
    async generate_prompt() {
      try {
        this.generation_loading = true;
        this.generation_loading_msg = "Generating Job...";
        const success = await this.generate_job_from_prompt({
          prompt: this.prompt
        });
        if (success) {
          this.show_description_modal = true;
        } else {
          this.root_error(
            this.$t("errors.job-post-failed") || "Failed to generate job"
          );
        }
      } catch (error: any) {
        this.root_error(error.message || "Internal server error");
      } finally {
        this.generation_loading = false;
        this.generation_loading_msg = "";
      }
    },
    on_modal_close() {
      this.show_description_modal = false;
      this.screening_question = true;
    },
    on_regenerate() {
      this.show_description_modal = false;
      this.prompt = "";
      this.set_ai_generated_prompt_resp(null);
    },
    async proceed_to_create_job(is_screening_question: boolean) {
      this.screening_question = false;
      if (is_screening_question) {
        const jobId = this.job_generation_prompt_resp?.job_id;
        if (jobId != null) {
          this.$router.push({
            name: "add-questions-to-standardised-job",
            params: { id: String(jobId) }
          });
        } else {
          this.root_error("Job ID missing. Please try again.");
          this.$router.push({ name: "recruiter-job-listing" });
        }
        return;
      }
      try {
        this.generation_loading = true;
        this.generation_loading_msg = "Activating job...";
        // Job already created via post-job/v2 (prompt) in generate_prompt; recommend_job_profiles already set
        const jid = this.job_generation_prompt_resp?.job_id;
        if (jid) {
          await this.activate_published_job(jid);
        }
        this.set_view_job_state(ViewJobState.FILE_JOB_POST);
        this.root_notification("Job posted successfully");
        await this.$router.push({ name: "recruiter-job-listing" });
      } catch (error: any) {
        this.root_error(error.message || "Internal server error");
      } finally {
        this.generation_loading = false;
        this.generation_loading_msg = "";
      }
    }
  }
});
